import {
  createContext,
  useContext,
  useState,
  useRef,
  type ReactNode,
} from 'react';

export type FooterMode = 'float' | 'fixed';

interface PageLayoutContextType {
  // Header — title can be string or a ReactNode (e.g. editable document title)
  title: string | ReactNode | null;
  setTitle: (title: string | ReactNode | null) => void;
  actionsRef: React.RefObject<HTMLDivElement | null>;
  // Footer
  footerRef: React.RefObject<HTMLDivElement | null>;
  footerMode: FooterMode;
  setFooterMode: (mode: FooterMode) => void;
}

const PageLayoutContext = createContext<PageLayoutContextType | undefined>(
  undefined,
);

export function PageLayoutProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | ReactNode | null>(null);
  const [footerMode, setFooterMode] = useState<FooterMode>('float');
  const actionsRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  const value: PageLayoutContextType = {
    title,
    setTitle,
    actionsRef,
    footerRef,
    footerMode,
    setFooterMode,
  };

  return (
    <PageLayoutContext.Provider value={value}>
      {children}
    </PageLayoutContext.Provider>
  );
}

export function usePageLayout() {
  const context = useContext(PageLayoutContext);
  if (!context) {
    throw new Error('usePageLayout must be used within PageLayoutProvider');
  }
  return context;
}

export function usePageHeader() {
  const { title, setTitle, actionsRef } = usePageLayout();
  return { title, setTitle, actionsRef };
}

export function usePageFooter() {
  const { footerRef, footerMode, setFooterMode } = usePageLayout();
  return { footerRef, footerMode, setFooterMode };
}
